import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../../../../../base/components/Header';
import {
    useAppDispatch,
    useAppSelector,
    useBrowserBackClickHandler,
    useHideWebsiteFooter,
} from '../../../../../base/hooks';
import { __t } from '../../../../../base/utils/t';
import { SignUpStep } from '../../enums';
import { RegistrationInfo } from '../../components/SignUp/RegistrationInfo';
import { CompanyInfo } from '../../components/SignUp/CompanyInfo';
import { CountriesSelect } from '../../components/SignUp/CountriesSelect';
import { AreasSelect } from '../../components/SignUp/AreasSelect';
import { Confirmation } from '../../components/SignUp/Confirmation';
import { Footer } from '../../components/SignUp/Footer';
import { signUpActions } from '../../redux/slices/signUpSlice';
import { Helmet } from '../../../../components/Helmet';

export const SignUpPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { step } = useAppSelector((state) => state.recruiterAppSignUp);
    const { referrer } = useAppSelector((state) => state.app);

    const [savedReferrerLocation, setSavedReferrerLocation] = useState('');

    useHideWebsiteFooter();

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0 });

        if (!document.referrer) {
            navigate('');
        }

        return function () {
            dispatch(signUpActions.clearData());
        };
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

        navigate(step === SignUpStep.RegistrationInfo ? '' : `step-${step + 1}`);
    }, [step]);

    useEffect(() => {
        if (location.state?.from?.pathname) {
            setSavedReferrerLocation(location.state?.from?.pathname);
        }
    }, [location]);

    const onBackClick = useCallback(() => {
        if (step === SignUpStep.RegistrationInfo) {
            if (savedReferrerLocation) {
                navigate(savedReferrerLocation);
                return;
            }
            if (referrer) {
                window.location.href = referrer;
                return;
            }
        }

        dispatch(signUpActions.setSignUpStep(step - 1));
    }, [step, referrer, savedReferrerLocation]);

    useBrowserBackClickHandler(onBackClick);

    const progressWidth = useMemo(() => {
        switch (step) {
            case SignUpStep.RegistrationInfo:
                return 33.2;
            case SignUpStep.CompanyInfo:
                return 49.8;
            case SignUpStep.Countries:
                return 66.4;
            case SignUpStep.Areas:
                return 83;
            case SignUpStep.Confirmation:
                return 100;
            default:
                return 0;
        }
    }, [step]);

    const nextLinkText = useMemo(() => {
        if (step === SignUpStep.Confirmation) {
            return __t('Зарегистрироваться');
        }

        return __t('Далее');
    }, [step]);

    const onNextClick = () => dispatch(signUpActions.submitSignUp());

    return (
        <>
            <Helmet pageName={__t('Регистрация для рекрутеров')} />
            <div className='recruiter-signup-page'>
                <Header title={__t('Регистрация для рекрутеров')} onBackClick={onBackClick} />
                <div className='recruiter-signup'>
                    <Routes>
                        <Route path='/' element={<RegistrationInfo />} />
                        <Route path='/step-2' element={<CompanyInfo />} />
                        <Route path='/step-3' element={<CountriesSelect />} />
                        <Route path='/step-4' element={<AreasSelect />} />
                        <Route path='/step-5' element={<Confirmation />} />
                    </Routes>
                </div>
                <Footer
                    backLinkText={__t('Назад')}
                    nextLinkText={nextLinkText}
                    progressWidth={progressWidth}
                    onBackClick={onBackClick}
                    onNextClick={onNextClick}
                />
            </div>
        </>
    );
};
